import { useTokenContext } from "@cyclos-io/swap-ui";
import { useWalletKit } from "@gokiprotocol/walletkit";
import {
  AppBar,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  Grid,
  Hidden,
  MenuItem,
  MenuList,
  Popover,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles";
import zIndex from "@material-ui/core/styles/zIndex";
import {
  AccountBalanceWalletOutlined,
  FiberManualRecord,
  FileCopyOutlined,
  FileCopyRounded,
  MoreVertRounded,
  OpenInNewRounded,
  PowerSettingsNewRounded,
} from "@material-ui/icons";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../assets/cyclos-logo.svg";
import SwapLogo from "../assets/cyclos-swap.svg";
import TextLogo from "../assets/cyclos-text-logo.svg";
import { CYSaddress, logoURI } from "../constants";
import useWallet from "../hooks/useWallet";
import AccountModal from "./Modals/account";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  swapLogo: {
    width: theme.spacing(20),
    maxHeight: theme.spacing(5), // to avaoid sudden size flicker
  },
  smallLogo: {
    width: theme.spacing(4),
    maxHeight: theme.spacing(5), // to avaoid sudden size flicker
  },
  navbar: {
    zIndex: zIndex.modal - 1300,
    background: theme.palette.background.paper,
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)",
    border: 0,
    borderBottom: `3px solid`,
    borderImageSlice: 1,
    borderImageSource: `linear-gradient(
      to right,
      rgb(192, 132, 252),
      rgb(167, 243, 208)
    )`,
  },
  navs: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  toggleButton: {
    minWidth: theme.spacing(10),
    paddingTop: theme.spacing(0.6),
    paddingBottom: theme.spacing(0.6),
    textTransform: "none",
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.text.secondary,
  },
  icon: {
    width: theme.spacing(2.7),
    height: theme.spacing(2.7),
    padding: theme.spacing(0.2),
  },
  banner: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    backgroundColor: theme.palette.primary.dark,
    color: "white",
    padding: theme.spacing(0.5),
    textAlign: "center",
  },
}));

const StyledToggleButtonGroup = withStyles((theme) => ({
  root: {
    "& .Mui-selected": {
      backgroundColor: "#13152033",
      color: theme.palette.primary.main,
    },
  },
  grouped: {
    margin: theme.spacing(0.5),
    border: "none",
    "&:not(:first-child)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-child": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}))(ToggleButtonGroup);

export default function ButtonAppBar() {
  const classes = useStyles();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const [accountModal, setAccountModal] = useState(false);
  const { connect } = useWalletKit();
  const { wallet, isConnected, disconnect, walletProvider, ICON } = useWallet();

  // Provider polls for fresh token values
  const { userTokens } = useTokenContext();

  const solBalance =
    userTokens && userTokens !== "fetching"
      ? userTokens?.["Ejmc1UB4EsES5oAaRN63SpoxMJidt3ZGBrqrZk49vjTZ"]
          ?.tokenAmount
      : 0;

  const cysBalance =
    userTokens && userTokens !== "fetching"
      ? userTokens?.[CYSaddress]?.tokenAmount
      : 0;

  const isSwapPage = location.pathname === "/";

  const handleInfoPopover = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const copyToClipboard = (txt: string) => {
    navigator.clipboard.writeText(txt);
    enqueueSnackbar("Address Copied", {
      variant: "success",
    });
  };

  return (
    <>
      {/* Acccount Details Modal */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleInfoPopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        style={{ marginTop: "0.8rem" }}
      >
        <MenuList>
          <MenuItem
            button
            divider
            onClick={() => {
              setAnchorEl(null);
              disconnect();
            }}
          >
            <PowerSettingsNewRounded
              color="primary"
              style={{ fontSize: "1rem" }}
            />
            &nbsp;&nbsp;&nbsp;
            <Typography variant="body2" color="primary">
              Disconnect
            </Typography>
          </MenuItem>
          <MenuItem
            button
            onClick={() => copyToClipboard(wallet?.publicKey ?? "")}
          >
            <FileCopyOutlined style={{ fontSize: "1rem" }} /> &nbsp;&nbsp;&nbsp;
            <Typography variant="body2">Copy Address</Typography>
          </MenuItem>
          <MenuItem
            button
            component={"a"}
            href={`https://solscan.io/account/${wallet?.publicKey}`}
            target="_blank"
          >
            <OpenInNewRounded style={{ fontSize: "1rem" }} /> &nbsp;&nbsp;&nbsp;
            <Typography variant="body2">View in Explorer</Typography>
          </MenuItem>
        </MenuList>
      </Popover>

      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        className={classes.navbar}
      >
        <Toolbar>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            className={classes.root}
          >
            <Grid item className={classes.navs}>
              <Link to="/" style={{ display: "flex", alignItems: "center" }}>
                {isConnected && isScreenSmall ? (
                  <img src={Logo} className={classes.smallLogo} alt="logo" />
                ) : isSwapPage ? (
                  <img src={SwapLogo} className={classes.swapLogo} alt="logo" />
                ) : (
                  <img src={TextLogo} className={classes.swapLogo} alt="logo" />
                )}
              </Link>

              {/* Top navigation will be replaced by bottom navigation in small screens */}
              <Hidden smDown>
                <StyledToggleButtonGroup
                  value={"/" + location.pathname.split("/")[1]}
                >
                  <ToggleButton
                    component={Link}
                    to="/"
                    value="/"
                    className={classes.toggleButton}
                  >
                    Swap
                  </ToggleButton>
                  <ToggleButton
                    component={Link}
                    to="/staking"
                    value="/staking"
                    className={classes.toggleButton}
                  >
                    Staking
                  </ToggleButton>
                  <ToggleButton
                    value=""
                    href="https://v2.cyclos.io/"
                    className={classes.toggleButton}
                    target="_blank"
                  >
                    Pools
                  </ToggleButton>
                  {/* <ToggleButton
                    value=""
                    href="https://dex.cyclos.io"
                    className={classes.toggleButton}
                    target="_blank"
                  >
                    DEX &nbsp;
                    <CallMade style={{ fontSize: "0.9rem" }} />
                  </ToggleButton> */}
                  <ToggleButton
                    value=""
                    href="https://buy.moonpay.com/?currencyCode=SOL"
                    className={classes.toggleButton}
                    target="_blank"
                  >
                    Buy Sol &nbsp;
                    <FiberManualRecord
                      style={{ fontSize: "0.4rem", marginBottom: "1rem" }}
                      color="primary"
                    />
                  </ToggleButton>
                </StyledToggleButtonGroup>
              </Hidden>
            </Grid>

            <Grid item>
              {wallet?.publicKey ? (
                <Box display="flex" style={{ gap: theme.spacing(1) }}>
                  {userTokens === "fetching" && (
                    <CircularProgress style={{ padding: 10 }} />
                  )}
                  <ButtonGroup variant="outlined" color="inherit" disableRipple>
                    <Button
                      endIcon={
                        <img
                          src={logoURI.cys}
                          alt="CYS"
                          className={classes.icon}
                        />
                      }
                    >
                      {cysBalance?.toFixed(2) ?? "-"}
                    </Button>
                    <Button
                      endIcon={
                        <img
                          src={logoURI.sol}
                          alt="SOL"
                          className={classes.icon}
                        />
                      }
                    >
                      {solBalance?.toFixed(3)}
                    </Button>
                  </ButtonGroup>
                  {isScreenSmall ? (
                    <Button
                      variant="contained"
                      // both min and max required
                      style={{
                        maxWidth: theme.spacing(4.4),
                        minWidth: theme.spacing(4.4),
                      }}
                      onClick={handleInfoPopover}
                    >
                      <MoreVertRounded />
                    </Button>
                  ) : (
                    <Button
                      startIcon={<AccountBalanceWalletOutlined />}
                      endIcon={
                        walletProvider?.name !== "Solong" ? (
                          <ICON
                            className={classes.icon}
                            style={{
                              padding: 0,
                              filter: "drop-shadow(3px 3px rgba(0,0,0, 0.2) )",
                            }}
                            alt={walletProvider?.name}
                          />
                        ) : (
                          <img
                            src={ICON}
                            className={classes.icon}
                            style={{
                              padding: 0,
                              filter: "drop-shadow(3px 3px rgba(0,0,0, 0.2) )",
                            }}
                            alt={walletProvider?.name}
                          />
                        )
                      }
                      className="reset-text"
                      color="secondary"
                      variant="contained"
                      onClick={handleInfoPopover}
                    >
                      {wallet?.publicKey?.toString().slice(0, 4) ?? ""}...
                    </Button>
                  )}
                </Box>
              ) : (
                <Button color="secondary" variant="contained" onClick={connect}>
                  Connect
                </Button>
              )}
            </Grid>
          </Grid>
        </Toolbar>
        <Divider />
      </AppBar>
    </>
  );
}
